import React, { useEffect, useMemo, useState } from "react";
import { ShoppingBag, X } from "lucide-react";
import { useCartStore } from "../store/useCartStore";
import { db, collection, getDocs } from "../firebase";
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { shopTranslations } from '../i18n/translations';

const cn = (...classes) => classes.filter(Boolean).join(" ");

const normalizeCategory = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-")
    .replace(/-+/g, "-");

const getProductKey = (product) =>
  String(product._docId ?? product.id ?? product.name);

const priceAfterDiscount = (product) => {
  const price = Number(product.price || 0);
  const discountRate = Number(product.discountRate || 0);

  if (!product.discountEligible) return price;

  return Number((price * (1 - discountRate)).toFixed(2));
};

const Shop = () => {
  const { language } = useLanguage();
  const t = createTranslator(language, shopTranslations);
  const [products, setProducts] = useState([]);
  const [shopCategories, setShopCategories] = useState([]);
  const [loadingShop, setLoadingShop] = useState(true);

  // La boutique s’ouvre directement sur BONS PLANS.
  const [activeCategory, setActiveCategory] = useState("bons-plans");

  const [selectedSizes, setSelectedSizes] = useState({});
  const [showPopup, setShowPopup] = useState(true);
  const [zoom, setZoom] = useState(null);

  const addToCart = useCartStore((state) => state.addToCart);

  useEffect(() => {
    const loadShop = async () => {
      try {
        setLoadingShop(true);

        const [productsSnapshot, categoriesSnapshot] = await Promise.all([
          getDocs(collection(db, "shop_products")),
          getDocs(collection(db, "shop_categories")),
        ]);

        const productsData = productsSnapshot.docs
          .map((docSnap) => {
            const data = docSnap.data();

            return {
              ...data,
              _docId: docSnap.id,
              id: data.id ?? docSnap.id,
            };
          })
          .filter(
            (product) =>
              product.archived !== true && product.visible !== false
          )
          .sort(
            (a, b) =>
              Number(a.id || 0) - Number(b.id || 0)
          );

        const categoriesData = categoriesSnapshot.docs
          .map((docSnap) => {
            const data = docSnap.data();

            return {
              ...data,
              _docId: docSnap.id,
              name: data.name || docSnap.id,
              slug: normalizeCategory(data.slug || docSnap.id),
              order: Number(data.order ?? 999),
            };
          })
          .filter(
            (category) =>
              category.archived !== true && category.visible !== false
          )
          .sort((a, b) => {
            const aIsBonsPlans =
              normalizeCategory(a.slug || a.name || a._docId) === "bons-plans";
            const bIsBonsPlans =
              normalizeCategory(b.slug || b.name || b._docId) === "bons-plans";

            if (aIsBonsPlans !== bIsBonsPlans) {
              return aIsBonsPlans ? -1 : 1;
            }

            if (a.order !== b.order) {
              return a.order - b.order;
            }

            return String(a.name).localeCompare(String(b.name), "fr");
          });

        setProducts(productsData);
        setShopCategories(categoriesData);
      } catch (error) {
        console.error("Erreur lors du chargement de la boutique :", error);
        setProducts([]);
        setShopCategories([]);
      } finally {
        setLoadingShop(false);
      }
    };

    loadShop();
  }, []);

  const visibleProducts = useMemo(() => {
    return products.filter(
      (product) =>
        product.archived !== true && product.visible !== false
    );
  }, [products]);

  /*
   * Ordre voulu :
   * 1. BONS PLANS
   * 2. Tous
   * 3. Les autres catégories, selon leur champ "order"
   */
  const tabs = useMemo(() => {
    const categories = shopCategories.map((category) => ({
      key: normalizeCategory(
        category.slug || category.name || category._docId
      ),
      label: category.name,
      category,
    }));

    const bonsPlans = categories.find(
      (category) => category.key === "bons-plans"
    );

    const otherCategories = categories.filter(
      (category) => category.key !== "bons-plans"
    );

    return [
      bonsPlans || {
        key: "bons-plans",
        label: t('deals'),
        category: {
          name: "BONS PLANS",
          slug: "bons-plans",
          _docId: "bons-plans",
        },
      },
      {
        key: "all",
        label: t('all'),
        category: null,
      },
      ...otherCategories,
    ];
  }, [shopCategories, language]);

  const activeTab = useMemo(() => {
    return tabs.find((tab) => tab.key === activeCategory) || tabs[0];
  }, [tabs, activeCategory]);

  const list = useMemo(() => {
    if (activeCategory === "all") {
      return visibleProducts;
    }

    const selectedCategory = activeTab?.category;

    const acceptedValues = new Set(
      [
        activeCategory,
        selectedCategory?.slug,
        selectedCategory?.name,
        selectedCategory?._docId,
      ]
        .filter(Boolean)
        .map(normalizeCategory)
    );

    return visibleProducts.filter((product) => {
      const productCategoryValues = [
        product.category,
        product.categorySlug,
        product.categoryId,
      ]
        .filter(Boolean)
        .map(normalizeCategory);

      return productCategoryValues.some((value) =>
        acceptedValues.has(value)
      );
    });
  }, [activeCategory, activeTab, visibleProducts]);

  const categoryLabelByAlias = useMemo(() => {
    const aliases = new Map();

    shopCategories.forEach((category) => {
      const label = category.name;

      [
        category.slug,
        category.name,
        category._docId,
      ]
        .filter(Boolean)
        .forEach((value) => {
          aliases.set(normalizeCategory(value), label);
        });
    });

    return aliases;
  }, [shopCategories]);

  const openZoom = (src, alt) => {
    if (!src) return;
    setZoom({ src, alt });
  };

  const closeZoom = () => {
    setZoom(null);
  };

  useEffect(() => {
    if (!zoom) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        closeZoom();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [zoom]);

  const handleSizeChange = (productKey, size) => {
    setSelectedSizes((previousSizes) => ({
      ...previousSizes,
      [productKey]: size,
    }));
  };

  const handleAdd = (product) => {
    const productKey = getProductKey(product);
    const needsSize =
      Array.isArray(product.sizes) && product.sizes.length > 0;

    if (needsSize && !selectedSizes[productKey]) {
      return;
    }

    addToCart({
      ...product,
      price: priceAfterDiscount(product),
      selectedSize: needsSize
        ? selectedSizes[productKey]
        : null,
    });
  };

  const headerCopy = activeCategory === 'all'
    ? { title: t('shop'), subtitle: t('shopSubtitle') }
    : activeCategory === 'bons-plans'
      ? { title: t('deals'), subtitle: t('dealsSubtitle') }
      : { title: activeTab?.label || t('shop'), subtitle: t('categorySubtitle') };

  return (
    <div className="relative w-full bg-white">
      <div className="h-8 md:h-14" />

      {showPopup && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4">
          <div className="max-w-md rounded-2xl border border-gray-200 bg-white p-6 text-center shadow-2xl">
            <h2 className="mb-4 text-xl font-extrabold text-[#4c2f8e]">
              {t('warning')}
            </h2>

            <p className="mb-6 leading-relaxed text-gray-700">
              {t('vouchers')}
              <br />

              <span className="text-sm text-gray-500">
                {t('oneItem')}
              </span>
            </p>

            <button
              type="button"
              onClick={() => setShowPopup(false)}
              className="w-full rounded-full bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] px-6 py-2 font-extrabold text-white shadow transition hover:opacity-95"
            >
              {t('understood')}
            </button>
          </div>
        </div>
      )}

      <div className="w-full">
        <div className="mx-auto w-full max-w-[1900px] px-3 pb-8 pt-2 sm:px-6 md:px-10">
          <div className="text-center">
            <h1 className="text-4xl font-black leading-[1.05] tracking-tight text-[#4c2f8e] md:text-6xl">
              {headerCopy.title}
            </h1>

            <p className="mx-auto mt-4 max-w-3xl text-sm leading-relaxed text-gray-600 md:text-base">
              {headerCopy.subtitle}
            </p>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {tabs.map((tab) => {
              const active = activeCategory === tab.key;

              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveCategory(tab.key)}
                  className={cn(
                    "h-11 rounded-full border px-6 text-xs font-extrabold uppercase tracking-wider transition",
                    active
                      ? "border-transparent bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] text-white shadow"
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:shadow-sm"
                  )}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <main className="w-full">
        <div className="mx-auto w-full max-w-[1900px] px-3 pb-12 sm:px-6 md:px-10">
          {loadingShop ? (
            <p className="text-center text-gray-500">
              {t('loadingProducts')}
            </p>
          ) : list.length === 0 ? (
            <p className="text-center text-gray-500">
              {t('noProducts')}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-5 md:grid-cols-3 md:gap-7 xl:grid-cols-4">
              {list.map((product) => {
                const productKey = getProductKey(product);
                const discounted = priceAfterDiscount(product);

                const needsSize =
                  Array.isArray(product.sizes) &&
                  product.sizes.length > 0;

                const sizeOk =
                  !needsSize ||
                  Boolean(selectedSizes[productKey]);

                const normalizedProductCategory =
                  normalizeCategory(product.category);

                const isRacket =
                  normalizedProductCategory === "raquettes";

                const isTextile =
                  normalizedProductCategory === "textiles";

                const categoryLabel =
                  categoryLabelByAlias.get(
                    normalizedProductCategory
                  ) ||
                  product.category ||
                  "";

                return (
                  <div
                    key={productKey}
                    className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm transition hover:shadow-2xl"
                  >
                    <div
                      className={cn(
                        "relative w-full overflow-hidden",
                        isRacket
                          ? "h-[300px] sm:h-[360px] md:h-[420px]"
                          : "",
                        isTextile
                          ? "h-[280px] sm:h-[340px] md:h-[400px]"
                          : "",
                        !isRacket && !isTextile
                          ? "h-[260px] sm:h-[320px] md:h-[380px]"
                          : ""
                      )}
                    >
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(76,47,142,0.08),transparent_55%)]" />
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(234,76,137,0.06),transparent_55%)]" />
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/0 via-white/0 to-white/20" />

                      <button
                        type="button"
                        onClick={() =>
                          openZoom(product.image, product.name)
                        }
                        className="group absolute inset-0 h-full w-full"
                        aria-label={t('enlarge', { name: product.name })}
                      >
                        <img
                          src={product.image}
                          alt={product.name}
                          loading="lazy"
                          className={cn(
                            "absolute inset-0 h-full w-full object-cover object-center",
                            "scale-[1.02] transform transition duration-300 group-hover:scale-[1.06]",
                            "drop-shadow-[0_18px_24px_rgba(0,0,0,0.10)]"
                          )}
                        />
                      </button>

                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleAdd(product);
                        }}
                        disabled={!sizeOk}
                        className={cn(
                          "absolute bottom-4 right-4 z-30 flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition md:hidden",
                          sizeOk
                            ? "bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] text-white hover:opacity-95"
                            : "cursor-not-allowed bg-gray-200 text-gray-400"
                        )}
                        aria-label={t('addCart')}
                      >
                        <ShoppingBag className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="px-5 pb-6 pt-5 md:px-6">
                      <div className="text-[11px] uppercase tracking-wider text-gray-500">
                        {categoryLabel}
                        {product.line ? ` • ${product.line}` : ""}
                      </div>

                      <div className="mt-2 line-clamp-2 text-sm font-extrabold uppercase tracking-wide text-gray-900 md:text-[15px]">
                        {product.name}
                      </div>

                      {product.description && (
                        <div className="mt-2 line-clamp-2 text-xs text-gray-500 md:text-sm">
                          {product.description}
                        </div>
                      )}

                      {Array.isArray(product.features) &&
                        product.features.length > 0 && (
                          <div className="mt-4 flex flex-col gap-2">
                            {product.features
                              .slice(0, 2)
                              .map((feature, index) => (
                                <div
                                  key={`${productKey}-feature-${index}`}
                                  className="rounded-2xl border border-gray-200 bg-white px-3 py-2 text-[11px] text-gray-700 md:text-xs"
                                >
                                  <span className="mr-2">
                                    {feature.icon}
                                  </span>

                                  {feature.text}
                                </div>
                              ))}
                          </div>
                        )}

                      <div className="mt-5 flex items-baseline gap-2">
                        {product.discountEligible && (
                          <span className="text-sm text-gray-400 line-through">
                            {Number(product.price || 0).toFixed(2)} €
                          </span>
                        )}

                        <span className="text-lg font-extrabold text-[#ea4c89] md:text-xl">
                          {Number(discounted || 0).toFixed(2)} €
                        </span>
                      </div>

                      {needsSize && (
                        <div className="mt-4">
                          <select
                            value={selectedSizes[productKey] || ""}
                            onChange={(event) =>
                              handleSizeChange(
                                productKey,
                                event.target.value
                              )
                            }
                            className="h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-[#ea4c89]/20"
                          >
                            <option value="">
                              {t('chooseSize')}
                            </option>

                            {product.sizes.map((size) => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => handleAdd(product)}
                        disabled={!sizeOk}
                        className={cn(
                          "mt-5 hidden h-11 w-full rounded-full text-sm font-extrabold shadow transition md:block",
                          !sizeOk
                            ? "cursor-not-allowed bg-gray-200 text-gray-400 shadow-none"
                            : "bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] text-white hover:opacity-95 hover:shadow-lg"
                        )}
                      >
                        {t('addCart')}
                      </button>

                      {needsSize && (
                        <div
                          className={cn(
                            "mt-3 text-xs",
                            sizeOk
                              ? "text-gray-400"
                              : "font-semibold text-[#ea4c89]"
                          )}
                        >
                          {sizeOk
                            ? " "
                            : t('sizeRequired')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-12 space-y-2 border-t border-gray-200 pt-7 text-center">
            <div className="text-sm text-gray-500">
              {t('delivery')}
            </div>

            <div className="text-sm text-gray-500">
              {t('cartTotalHint')}
            </div>

            <div className="mt-3 text-xs text-gray-500">
              {t('purchasing')}{" "}
              <a
                href="/terms-of-sale"
                className="text-[#4c2f8e] underline transition hover:text-[#ea4c89]"
              >
                {t('terms')}
              </a>
              .
            </div>
          </div>
        </div>
      </main>

      {zoom && (
        <div className="fixed inset-0 z-[999]">
          <button
            type="button"
            onClick={closeZoom}
            className="absolute inset-0 bg-black/70"
            aria-label={t('closePreview')}
          />

          <div className="absolute inset-0 flex items-center justify-center p-4 sm:p-8">
            <div className="relative w-full max-w-6xl">
              <button
                type="button"
                onClick={closeZoom}
                className="absolute -right-3 -top-3 flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 bg-white/95 shadow transition hover:bg-white sm:right-0 sm:top-0"
                aria-label={t('close')}
              >
                <X className="h-5 w-5 text-gray-800" />
              </button>

              <div className="overflow-hidden rounded-3xl border border-white/40 bg-white shadow-2xl">
                <div className="relative h-[75vh] w-full bg-white">
                  <img
                    src={zoom.src}
                    alt={zoom.alt}
                    className="absolute inset-0 h-full w-full object-contain"
                  />
                </div>

                <div className="border-t border-gray-100 px-5 py-4">
                  <div className="line-clamp-2 text-sm font-extrabold uppercase tracking-wide text-[#4c2f8e]">
                    {zoom.alt}
                  </div>

                  <div className="mt-1 text-xs text-gray-500">
                    {t('previewHint')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Shop;
